import { useState } from "react";
import SelectUnit from "./SelectUnit";
import useOperationContext from "./useOperationContext";
import useGetCurrentQuoteRevision from "@/lib/SelectedQuoteRevisionIdContext/useGetCurrentQuoteRevision";
import { unitOptions, UnitType } from "./unit";
import { formatNumber, getIn, setIn } from "@/lib/utils";
import { AnyUnit, convertUnit, LengthUnit } from "@/lib/units";
import getValidationErrors from "./getValidationErrors";
import ValidationErrorsTooltip from "./ValidationErrorsTooltip";
import useDisplayLengthUnit from "./useDisplayLengthUnit";
import classnames from "classnames";
import RepeatIcon from "~icons/fa6-solid/repeat";

type CalculatorValue =
  | {
      value: string;
      unit?: UnitType;
      nunit?: UnitType;
      dunit?: UnitType;
    }
  | null
  | string;

export default function Input({
  label,
  keyPath,
  unit,
  nunit,
  dunit,
  width,
  alignRight,
  suffix,
  hideUnit = false,
  transformFns,
  className,
  markedForReuse = false,
  toggleFieldReuse,
}: {
  label?: string;
  keyPath: readonly PropertyKey[];
  unit?: UnitType;
  nunit?: UnitType;
  dunit?: UnitType;
  width?: number;
  alignRight?: boolean;
  suffix?: string;
  hideUnit?: boolean;
  transformFns?: {
    initialValue: (value: string | null) => string | null;
    saveValue: (value: string | null) => string | null;
  };
  className?: string;
  markedForReuse?: boolean;
  toggleFieldReuse?: () => void;
}) {
  const hasUnit = unit || nunit || dunit;
  const tooltipId = Math.random().toString();

  const { operationData: data, updateOperation } = useOperationContext();
  const { id, draft, augmented, validation_errors } = data;
  const draftProp = getIn(draft, keyPath, null) as CalculatorValue;
  const augmentedProp = getIn(augmented, keyPath, null) as CalculatorValue;

  const draftValue =
    hasUnit && typeof draftProp === "object" ? draftProp?.value : draftProp;
  const augmentedValue =
    hasUnit && typeof augmentedProp === "object"
      ? augmentedProp?.value
      : augmentedProp;

  const {
    displayLengthUnit,
    unitKey: convertedUnitKey,
    setOverrideDisplayUnit,
    toDisplayUnit,
  } = useDisplayLengthUnit({
    keyPath,
    // conversion handled outside of this component because units are hidden and UX is different
    skipConversion: hideUnit,
    unit,
    nunit,
    dunit,
  });

  const tempInitialValue = (draftValue ?? augmentedValue) as string | null;
  const initialValue =
    toDisplayUnit?.(tempInitialValue) ??
    transformFns?.initialValue(tempInitialValue) ??
    tempInitialValue;

  const draftUnit = unit && typeof draftProp === "object" && draftProp?.unit;
  const draftNunit = nunit && typeof draftProp === "object" && draftProp?.nunit;
  const draftDunit = dunit && typeof draftProp === "object" && draftProp?.dunit;

  const augmentedUnit =
    unit && typeof augmentedProp === "object" && augmentedProp?.unit;
  const augmentedNunit =
    nunit && typeof augmentedProp === "object" && augmentedProp?.nunit;
  const augmentedDunit =
    dunit && typeof augmentedProp === "object" && augmentedProp?.dunit;

  const unitValue =
    (convertedUnitKey === "unit" ? displayLengthUnit : null) ??
    draftUnit ??
    augmentedUnit;
  const nunitValue =
    (convertedUnitKey === "nunit" ? displayLengthUnit : null) ??
    draftNunit ??
    augmentedNunit;
  const dunitValue =
    (convertedUnitKey === "dunit" ? displayLengthUnit : null) ??
    draftDunit ??
    augmentedDunit;

  const [input, setInput] = useState<string | null>(initialValue);
  const [editing, setEditing] = useState(false);

  const errorMessages = getValidationErrors(validation_errors, keyPath);
  const canEditQuoteRevision = useGetCurrentQuoteRevision()?.can_edit;

  return (
    <>
      <div
        className={classnames(
          "flex flex-col justify-center items-start text-sm text-gray-800 rounded-sm relative group",
          {
            "h-7.5 px-1 pb-1 mt-1": label,
            "h-4.5": !label,
            "border border-red-600": errorMessages.length > 0,
            "focus-within:border focus-within:border-blue-600 border border-transparent":
              errorMessages.length === 0,
          },
        )}
        style={{ width: width ?? (label ? 80 : 60) }}
        data-tooltip-id={tooltipId}
      >
        {label && (
          <div className="px-1 text-xs text-gray-400 mt-[-8px] bg-white">
            {label}
          </div>
        )}
        {toggleFieldReuse &&
          canEditQuoteRevision &&
          errorMessages.length === 0 && (
            <>
              <div
                className={classnames(
                  "absolute top-2.5 left-1 text-gray-400 hover:text-gray-600 group-hover:block cursor-pointer",
                  {
                    hidden: !markedForReuse,
                  },
                )}
                title="Reuse next time"
                onClick={() => toggleFieldReuse()}
              >
                <RepeatIcon className="size-3" />
              </div>
            </>
          )}
        <div className={`w-full flex ${draftValue && "text-blue-600"}`}>
          {(unit === "currency" || nunit === "currency") && (
            <span className="px-0.5">$</span>
          )}
          <div className="grow">
            <input
              tabIndex={errorMessages.length === 0 ? -1 : 0}
              disabled={!canEditQuoteRevision}
              className={classnames(
                "w-full focus:outline-none truncate",
                className,
                {
                  "text-right": doAlignRight(),
                  "pl-3": toggleFieldReuse,
                },
              )}
              title={editing ? "" : formatNumber(initialValue)}
              value={
                editing ? (input ?? "") : (formatNumber(initialValue) ?? "")
              }
              onFocus={() => {
                setEditing(true);
                setInput(initialValue);
              }}
              onBlur={() => {
                setEditing(false);
                saveEditIfValueIsChanged();
              }}
              onKeyUp={(e) => {
                if (e.key === "Enter") {
                  e.currentTarget.blur();
                } else if (e.key === "Escape") {
                  setInput(initialValue);
                }
              }}
              onChange={(e) => setInput(e.target.value)}
            />
          </div>
          {!hideUnit && unit && unit !== "currency" && unitValue && (
            <SelectUnit
              disabled={!canEditQuoteRevision || unitOptions[unit].length === 1}
              value={unitValue}
              options={unitOptions[unit]}
              onSelect={(value) => {
                if (setOverrideDisplayUnit) {
                  // mutate display unit for this user / "session" locally, do not update the `draft` in the DB
                  setOverrideDisplayUnit(value as LengthUnit);
                } else {
                  updateUnitWithConversion("unit", unitValue, value);
                }
              }}
            />
          )}
          {nunit && nunit !== "currency" && nunitValue && (
            <SelectUnit
              disabled={!canEditQuoteRevision}
              value={nunitValue}
              options={unitOptions[nunit]}
              onSelect={(value) => {
                if (setOverrideDisplayUnit) {
                  // mutate display unit for this user / "session" locally, do not update the `draft` in the DB
                  setOverrideDisplayUnit(value as LengthUnit);
                } else {
                  updateUnitWithConversion("nunit", nunitValue, value);
                }
              }}
            />
          )}
          {dunit && dunitValue && (
            <SelectUnit
              disabled={!canEditQuoteRevision}
              dunit
              value={dunitValue}
              options={unitOptions[dunit]}
              onSelect={(value) => {
                if (setOverrideDisplayUnit) {
                  // mutate display unit for this user / "session" locally, do not update the `draft` in the DB
                  setOverrideDisplayUnit(value as LengthUnit);
                } else {
                  updateUnitWithConversion("dunit", dunitValue, value);
                }
              }}
            />
          )}
          {suffix}
        </div>
      </div>
      <ValidationErrorsTooltip
        tooltipId={tooltipId}
        errorMessages={errorMessages}
        showTooltip={errorMessages.length > 0 && !!draftValue}
      />
    </>
  );

  function updateUnitWithConversion(
    unitKey: string,
    fromUnit: string,
    toUnit: string,
  ) {
    if (unitKey === "dunit" && !draftNunit && augmentedNunit) {
      setIn(draft, [...keyPath, "nunit"], augmentedNunit);
    } else if (unitKey === "nunit" && !draftDunit && augmentedDunit) {
      setIn(draft, [...keyPath, "dunit"], augmentedDunit);
    }

    setIn(draft, [...keyPath, unitKey], toUnit);
    const inputAsNumber = Number(input);
    const convertedValue = Number.isNaN(inputAsNumber)
      ? input
      : convertUnit(inputAsNumber, fromUnit as AnyUnit, toUnit as AnyUnit);
    setInput(convertedValue?.toString() ?? null);
    setIn(draft, [...keyPath, "value"], convertedValue);
    updateOperation(id, draft);
  }

  function saveEditIfValueIsChanged() {
    if (input !== initialValue) {
      updateOperation(id, setIn(draft, keyPath as string[], getSaveEditData()));
    }
  }

  function getSaveEditData() {
    // send null instead of an empty string for recalculation on the backend
    const nonEmptyStringOrNull = input || null;
    const value =
      transformFns?.saveValue(nonEmptyStringOrNull) ?? nonEmptyStringOrNull;
    if (hasUnit) {
      if (unit) {
        return { value, unit: unitValue };
      } else if (dunit && nunit) {
        return {
          value,
          dunit: dunitValue,
          nunit: nunitValue,
        };
      }
    } else {
      return value;
    }
  }

  function doAlignRight() {
    return alignRight || unit || nunit || dunit || suffix;
  }
}
